import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, TrendingUp } from 'lucide-react';
import { getProducts } from '../../api/services';
import SafeImage from '../ui/SafeImage';
import { formatPrice } from '../../utils/format';

const SUGGESTIONS = ['Mantas', 'Almohadón lino', 'Cubrecama', 'Terracota', 'Camino de mesa'];

export default function SearchModal({ open, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
    else { setQuery(''); setResults([]); }
  }, [open]);

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const data = await getProducts({ search: query, limit: 6 });
        setResults(data.items);
      } catch { setResults([]); }
      setLoading(false);
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const go = (slug) => { onClose(); navigate(`/producto/${slug}`); };
  const search = (term) => { onClose(); navigate(`/catalogo?buscar=${encodeURIComponent(term)}`); };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose} className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-sm" />
          <motion.div
            initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            className="fixed inset-x-0 top-0 z-50 mx-auto max-w-2xl p-4"
          >
            <div className="overflow-hidden rounded-2xl bg-mist-50 shadow-2xl dark:bg-ink">
              <form
                onSubmit={(e) => { e.preventDefault(); if (query.trim()) search(query); }}
                className="flex items-center gap-3 border-b border-ink/10 px-5 py-4 dark:border-white/10"
              >
                <Search size={20} className="text-ink/40" />
                <input
                  ref={inputRef} value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar productos, materiales, colores..."
                  className="flex-1 bg-transparent text-base outline-none placeholder:text-ink/40"
                />
                <button type="button" onClick={onClose} className="text-ink/40 hover:text-ink"><X size={20} /></button>
              </form>

              <div className="max-h-[60vh] overflow-y-auto p-2">
                {query.length < 2 ? (
                  <div className="p-4">
                    <p className="mb-3 flex items-center gap-2 text-xs uppercase tracking-wider text-ink/40">
                      <TrendingUp size={14} /> Búsquedas populares
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {SUGGESTIONS.map((s) => (
                        <button key={s} onClick={() => search(s)} className="chip border border-ink/15 hover:border-ink hover:bg-ink hover:text-mist-50 dark:border-white/15">
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : loading ? (
                  <div className="space-y-2 p-2">
                    {[1, 2, 3].map((i) => <div key={i} className="skeleton h-16 rounded-xl" />)}
                  </div>
                ) : results.length === 0 ? (
                  <p className="p-8 text-center text-sm text-ink/50">Sin resultados para "{query}"</p>
                ) : (
                  results.map((p) => (
                    <button key={p._id} onClick={() => go(p.slug)} className="flex w-full items-center gap-4 rounded-xl p-2 text-left transition hover:bg-ink/5 dark:hover:bg-white/5">
                      <SafeImage src={p.images?.[0]} seed={p.slug} alt={p.name} className="h-14 w-14 flex-shrink-0 rounded-lg" />
                      <div className="flex-1">
                        <p className="font-serif text-base">{p.name}</p>
                        <p className="text-xs text-ink/50">{p.category?.name}</p>
                      </div>
                      <span className="text-sm font-medium">{formatPrice(p.price)}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
